<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1150] flex items-center justify-center p-2.5 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-lg bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/80 dark:border-[#263449] overflow-hidden transform transition-all flex flex-col max-h-[92vh] sm:max-h-[88vh]"
    >
      <!-- Modal Header -->
      <div
        class="px-5 sm:px-6 py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between flex-shrink-0"
      >
        <div class="flex items-center space-x-3">
          <div
            class="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 flex-shrink-0 text-amber-400"
          >
            <Scale class="w-5 h-5" />
          </div>
          <div>
            <h3 class="text-base sm:text-lg font-bold text-white">
              Fee Debit & Credit Adjustment
            </h3>
            <p class="text-[11px] sm:text-xs text-indigo-200 font-medium">
              Add charges (ID card, badge) or grant concessions & credits
            </p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="p-1.5 sm:p-2 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Student Banner -->
      <div
        v-if="student"
        class="bg-indigo-50/70 dark:bg-[#18243A] border-b border-indigo-100 dark:border-[#263449] px-4 sm:px-6 py-2.5 flex items-center justify-between flex-shrink-0"
      >
        <div>
          <span class="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider block">
            Student Account
          </span>
          <h4 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-[#F1F5F9]">
            {{ student.name }}
          </h4>
        </div>
        <div class="text-right">
          <span class="text-[11px] text-slate-500 dark:text-[#A6B4C8]">
            Roll: <strong class="font-mono text-slate-700 dark:text-slate-300">{{ student.rollNumber || "N/A" }}</strong>
          </span>
          <p class="text-[11px] font-medium text-slate-600 dark:text-[#A6B4C8]">
            {{ student.class?.name }}{{ student.class?.section ? ` (${student.class.section})` : "" }}
          </p>
        </div>
      </div>

      <!-- Form Body -->
      <form @submit.prevent="handleSubmit" class="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div class="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-4">
          <!-- Type Toggle: Debit vs Credit -->
          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wide mb-1.5">
              Adjustment Type
            </label>
            <div class="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                @click="setType('debit')"
                :class="[
                  'p-3 rounded-xl border-2 text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-2 cursor-pointer',
                  form.type === 'debit'
                    ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 shadow-xs'
                    : 'border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:border-slate-300',
                ]"
              >
                <PlusCircle class="w-4 h-4 text-rose-500" />
                <span>Debit (Charge Fee)</span>
              </button>
              <button
                type="button"
                @click="setType('credit')"
                :class="[
                  'p-3 rounded-xl border-2 text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-2 cursor-pointer',
                  form.type === 'credit'
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 shadow-xs'
                    : 'border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:border-slate-300',
                ]"
              >
                <MinusCircle class="w-4 h-4 text-emerald-500" />
                <span>Credit (Concession)</span>
              </button>
            </div>
          </div>

          <!-- Preset Item Selection Chips -->
          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wide mb-1.5">
              {{ form.type === 'debit' ? 'Select Charge Item' : 'Select Concession Reason' }}
            </label>

            <!-- Debit Preset Chips -->
            <div v-if="form.type === 'debit'" class="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                v-for="item in debitPresets"
                :key="item.id"
                type="button"
                @click="selectDebitPreset(item)"
                :class="[
                  'p-2 rounded-xl border text-xs font-bold transition flex flex-col items-center justify-center gap-1 cursor-pointer text-center',
                  form.itemCategory === item.id
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 shadow-xs ring-2 ring-indigo-500/20'
                    : 'border-slate-200 dark:border-[#263449] bg-slate-50 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#1E293B]',
                ]"
              >
                <span class="text-base">{{ item.icon }}</span>
                <span class="truncate w-full">{{ item.name }}</span>
                <span v-if="item.defaultAmount > 0" class="text-[10px] text-slate-500 dark:text-slate-400 font-mono">
                  ₹{{ item.defaultAmount }}
                </span>
                <span v-else class="text-[10px] text-indigo-500 dark:text-indigo-400">Custom</span>
              </button>
            </div>

            <!-- Credit Preset Chips -->
            <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <button
                v-for="item in creditPresets"
                :key="item.id"
                type="button"
                @click="selectCreditPreset(item)"
                :class="[
                  'p-2 rounded-xl border text-xs font-bold transition flex flex-col items-center justify-center gap-1 cursor-pointer text-center',
                  form.itemCategory === item.id
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 shadow-xs ring-2 ring-emerald-500/20'
                    : 'border-slate-200 dark:border-[#263449] bg-slate-50 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#1E293B]',
                ]"
              >
                <span class="text-base">{{ item.icon }}</span>
                <span class="truncate w-full">{{ item.name }}</span>
              </button>
            </div>
          </div>

          <!-- Title & Amount Inputs -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <!-- Title / Particulars -->
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Item Particulars / Title <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.title"
                type="text"
                required
                placeholder="e.g. ID Card, School Badge, Late Fine..."
                class="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>

            <!-- Amount Input -->
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Amount (₹) <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold text-xs">
                  ₹
                </span>
                <input
                  v-model.number="form.amount"
                  type="number"
                  min="0.01"
                  step="1"
                  required
                  placeholder="0"
                  class="w-full pl-7 pr-3 py-2 text-xs font-black rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none font-mono"
                />
              </div>
            </div>
          </div>

          <!-- Options for Debit: Pay Now vs Pay Later -->
          <div v-if="form.type === 'debit'" class="p-3.5 rounded-xl bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] space-y-2.5">
            <label class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wide">
              Payment Settlement
            </label>
            <div class="grid grid-cols-2 gap-2">
              <button
                type="button"
                @click="form.paidNow = false"
                :class="[
                  'py-2 px-3 text-xs font-bold rounded-lg border transition cursor-pointer text-center',
                  !form.paidNow
                    ? 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800'
                    : 'bg-white dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]',
                ]"
              >
                Add to Dues (Pay Later)
              </button>
              <button
                type="button"
                @click="form.paidNow = true"
                :class="[
                  'py-2 px-3 text-xs font-bold rounded-lg border transition cursor-pointer text-center',
                  form.paidNow
                    ? 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
                    : 'bg-white dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]',
                ]"
              >
                Collect Now (With Receipt)
              </button>
            </div>

            <!-- Payment Method (if paidNow) -->
            <div v-if="form.paidNow" class="pt-2 border-t border-slate-200/60 dark:border-[#263449] flex items-center gap-3">
              <label class="text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] shrink-0">
                Payment Method:
              </label>
              <select
                v-model="form.paymentMethod"
                class="flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#111C30] text-slate-800 dark:text-[#F1F5F9] outline-none"
              >
                <option value="Cash">Cash</option>
                <option value="UPI">UPI / QR</option>
                <option value="Bank transfer">Bank Transfer</option>
                <option value="Card">Debit / Credit Card</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <!-- Options for Credit: Apply To -->
          <div v-else class="p-3.5 rounded-xl bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] space-y-2">
            <label class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wide">
              Apply Credit Concession To:
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                @click="form.applyTo = 'oldest_month'"
                :class="[
                  'py-2 px-2.5 text-xs font-bold rounded-lg border transition cursor-pointer text-center',
                  form.applyTo === 'oldest_month'
                    ? 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
                    : 'bg-white dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]',
                ]"
              >
                Overdue Monthly Fees
              </button>
              <button
                type="button"
                @click="form.applyTo = 'admission'"
                :class="[
                  'py-2 px-2.5 text-xs font-bold rounded-lg border transition cursor-pointer text-center',
                  form.applyTo === 'admission'
                    ? 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
                    : 'bg-white dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]',
                ]"
              >
                Admission Fee Balance
              </button>
              <button
                type="button"
                @click="form.applyTo = 'general'"
                :class="[
                  'py-2 px-2.5 text-xs font-bold rounded-lg border transition cursor-pointer text-center',
                  form.applyTo === 'general'
                    ? 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
                    : 'bg-white dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]',
                ]"
              >
                General Account Credit
              </button>
            </div>
          </div>

          <!-- Date & Remarks -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Date
              </label>
              <input
                v-model="form.date"
                type="date"
                class="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] outline-none"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Remarks / Reference Note
              </label>
              <input
                v-model="form.remarks"
                type="text"
                placeholder="Optional explanation..."
                class="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] outline-none"
              />
            </div>
          </div>
        </div>

        <!-- Action Footer -->
        <div class="flex-shrink-0 px-4 sm:px-6 py-3.5 bg-slate-50 dark:bg-[#18243A] border-t border-slate-200 dark:border-[#263449] flex items-center justify-end space-x-3">
          <button
            type="button"
            @click="$emit('close')"
            :disabled="isSubmitting"
            class="px-4 py-2 text-xs font-bold text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting || !form.amount"
            class="px-5 py-2 text-xs font-bold text-white rounded-xl shadow-lg transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
            :class="
              form.type === 'debit'
                ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/25'
                : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/25'
            "
          >
            <AppLoader v-if="isSubmitting" variant="button" class="w-4 h-4 text-white" />
            <span>
              {{
                isSubmitting
                  ? "Recording..."
                  : form.type === "debit"
                  ? form.paidNow
                    ? `Collect ₹${(form.amount || 0).toLocaleString('en-IN')}`
                    : `Debit ₹${(form.amount || 0).toLocaleString('en-IN')}`
                  : `Apply Credit ₹${(form.amount || 0).toLocaleString('en-IN')}`
              }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { reactive, watch } from "vue";
  import { X, Scale, PlusCircle, MinusCircle } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    student: { type: Object, default: null },
    isSubmitting: { type: Boolean, default: false },
  });

  const emit = defineEmits(["close", "submit"]);

  const todayStr = new Date().toISOString().split("T")[0];

  const debitPresets = [
    { id: "id_card", name: "ID Card", defaultAmount: 200, icon: "🪪" },
    { id: "badge", name: "School Badge", defaultAmount: 200, icon: "🏷️" },
    { id: "uniform", name: "Uniform / Belt", defaultAmount: 350, icon: "👔" },
    { id: "books", name: "Books / Diary", defaultAmount: 400, icon: "📖" },
    { id: "exam_fee", name: "Exam / Lab Fee", defaultAmount: 250, icon: "📝" },
    { id: "late_fine", name: "Late Fine", defaultAmount: 100, icon: "⚠️" },
    { id: "transport", name: "Transport Fee", defaultAmount: 500, icon: "🚌" },
    { id: "other", name: "Other Item", defaultAmount: 0, icon: "✏️" },
  ];

  const creditPresets = [
    { id: "discount_concession", name: "Special Concession", icon: "🎁" },
    { id: "scholarship", name: "Merit Scholarship", icon: "🎓" },
    { id: "sibling_concession", name: "Sibling Concession", icon: "👨‍👩‍👧" },
    { id: "fee_waiver", name: "Fee Waiver / Correction", icon: "⚖️" },
    { id: "other", name: "Other Credit", icon: "✏️" },
  ];

  const form = reactive({
    type: "debit", // "debit" | "credit"
    itemCategory: "id_card",
    title: "ID Card",
    amount: 200,
    paidNow: false,
    paymentMethod: "Cash",
    applyTo: "oldest_month",
    date: todayStr,
    remarks: "",
  });

  const setType = (t) => {
    form.type = t;
    if (t === "debit") {
      selectDebitPreset(debitPresets[0]);
    } else {
      selectCreditPreset(creditPresets[0]);
    }
  };

  const selectDebitPreset = (item) => {
    form.itemCategory = item.id;
    if (item.id === "other") {
      form.title = "";
      form.amount = 0;
    } else {
      form.title = item.name;
      form.amount = item.defaultAmount;
    }
  };

  const selectCreditPreset = (item) => {
    form.itemCategory = item.id;
    if (item.id === "other") {
      form.title = "";
    } else {
      form.title = item.name;
    }
  };

  watch(
    () => props.show,
    (isOpen) => {
      if (isOpen) {
        form.type = "debit";
        form.itemCategory = "id_card";
        form.title = "ID Card";
        form.amount = 200;
        form.paidNow = false;
        form.paymentMethod = "Cash";
        form.applyTo = "oldest_month";
        form.date = todayStr;
        form.remarks = "";
      }
    }
  );

  const handleSubmit = () => {
    emit("submit", {
      type: form.type,
      itemCategory: form.itemCategory,
      title: form.title,
      amount: form.amount,
      paidNow: form.paidNow,
      paymentMethod: form.paymentMethod,
      applyTo: form.applyTo,
      date: form.date,
      remarks: form.remarks,
    });
  };
</script>
